import { useEffect, useRef, useState } from "react";

import { calculateReadingAccuracy, useSpeechRecognition } from "../../../../hooks/useSpeechRecognition";

// 리더스북 따라 말하기(2026-10-17). 지인에듀가 지문 음원을 문장마다 잘라 준다(audio.sentences, version 2).
// 문장 하나를 듣고(▶) 마이크(🎤)를 눌러 따라 읽으면, 직독직해와 같은 받아쓰기 정확도로 점수를 낸다.
//
// - 마이크는 반드시 아이가 누른 자리에서 연다(iOS 는 사용자 동작 밖에서 열면 조용히 막힌다).
// - 브라우저가 받아쓰기를 못 하면(파이어폭스, 일부 앱 안 브라우저) 마이크 단추를 감추고 듣기만 둔다.
// - 점수는 첫 시도를 따로 남긴다(calculate_grade 가 첫 시도로 센다). 화면에는 **가장 잘 읽은 점수**를 보인다.
//   다시 읽다가 못 읽으면 좋은 점수가 지워진 것처럼 보였다(2026-10-17 대표).
// - 받아쓴 글은 아이에게 보이지 않는다. 점수만 보인다(대표).
// - **쉼표에서 끊지 않는다.** 한 번 듣기(continuous 꺼짐)는 크롬이 짧게 쉬면 거기서 끝내서 문장 앞부분만
//   받았다("…tiny lights but" 64점). 이어 듣다가 문장 낱말을 다 들으면 스스로 끝내고, 아니면 '다 읽었어요' 로 끝낸다.
// - 다른 문장을 읽은 것이면 점수를 매기지 않는다. 듣는 중에 아이가 다음 줄을 읽어 버려 엉뚱한 줄에 점수가 붙었다.

// 문장 낱말 수에 맞춘 듣기 상한. 짧은 문장도 넉넉히 기다린다.
const listenMillisecondsOf = (text) => Math.max(8000, String(text).split(/\s+/).length * 900);
// 이 정확도가 되면 다 읽은 것으로 보고 듣기를 끝낸다
const DONE_ACCURACY = 95;
// 다른 문장이 이만큼 더 잘 맞으면 그 문장을 읽은 것으로 본다
const OTHER_SENTENCE_MARGIN = 30;

// 마이크 그림. 🎤 는 그림 글꼴이 없는 PC 에서 네모로 보여서 선으로 그린다(해설지 readers_pdf 의 MIC_SVG 와 같은 모양).
export const MicIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4M8 22h8" />
  </svg>
);

export const speakingScoreClass = (accuracy) => {
  if (accuracy >= 90) return "is-great";
  if (accuracy >= 70) return "is-good";
  return "is-low";
};

const SpeakingPanel = ({ audio, speakingMap, onResult, isEditable, onClose }) => {
  const { isSupported, listen, finishListening, stopListening } = useSpeechRecognition();
  const [listeningIndex, setListeningIndex] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const playerRef = useRef(null);

  useEffect(
    () => () => {
      stopListening();
      if (playerRef.current) playerRef.current.pause();
    },
    [stopListening]
  );

  const play = (sentence) => {
    if (!sentence.mp3) return;
    if (playerRef.current) playerRef.current.pause();
    const player = new Audio(sentence.mp3);
    playerRef.current = player;
    setPlayingIndex(sentence.idx);
    player.onended = () => setPlayingIndex(null);
    player.onerror = () => setPlayingIndex(null);
    player.play().catch(() => setPlayingIndex(null));
  };

  const record = async (sentence) => {
    if (listeningIndex !== null) {
      // 듣는 중에 다시 누르면 '다 읽었어요' 다. 여기까지 들은 것으로 채점한다.
      finishListening();
      return;
    }
    if (playerRef.current) playerRef.current.pause();
    setListeningIndex(sentence.idx);
    const { transcript } = await listen({
      language: "en-US",
      continuous: true,
      maximumMilliseconds: listenMillisecondsOf(sentence.text),
      onTranscript: (heard) => {
        if (calculateReadingAccuracy(sentence.text, heard) >= DONE_ACCURACY) finishListening();
      },
    });
    setListeningIndex(null);
    if (!transcript) {
      alert("잘 안 들렸어요. 마이크를 다시 누르고 또박또박 읽어 주세요.");
      return;
    }
    const accuracy = calculateReadingAccuracy(sentence.text, transcript);
    const otherBest = Math.max(
      0,
      ...audio.sentences
        .filter((other) => other.idx !== sentence.idx)
        .map((other) => calculateReadingAccuracy(other.text, transcript))
    );
    if (otherBest >= accuracy + OTHER_SENTENCE_MARGIN) {
      alert("다른 문장을 읽은 것 같아요. 이 문장을 다시 읽어 주세요.");
      return;
    }
    onResult(sentence.idx, accuracy, transcript);
  };

  const shownScoreOf = (spoken) => spoken?.best_accuracy ?? spoken?.last_accuracy;
  const scoreList = audio.sentences
    .map((sentence) => shownScoreOf(speakingMap[sentence.idx]))
    .filter((accuracy) => typeof accuracy === "number");
  const average = scoreList.length ? Math.round(scoreList.reduce((sum, one) => sum + one, 0) / scoreList.length) : null;

  return (
    <div className="speaking">
      <div className="speaking__head">
        <div>
          <div className="speaking__title">한 문장씩 듣고 따라 말하기</div>
          <div className="speaking__sub">
            {scoreList.length}/{audio.sentences.length} 문장
            {average !== null && <span className={`speaking__average ${speakingScoreClass(average)}`}>평균 {average}점</span>}
          </div>
        </div>
        <button type="button" className="speaking__close" onClick={onClose} aria-label="닫기">
          ×
        </button>
      </div>
      <div className="speaking__list">
        {audio.sentences.map((sentence) => {
          const spoken = speakingMap[sentence.idx];
          const isListening = listeningIndex === sentence.idx;
          return (
            <div className={`speaking__row${isListening ? " is-listening" : ""}`} key={sentence.idx}>
              <div className="speaking__text">
                {sentence.speaker && sentence.speaker !== "narrator" && <b>{sentence.speaker}: </b>}
                <span className={sentence.speaker === "narrator" ? "speaking__narration" : ""}>{sentence.text}</span>
              </div>
              <div className="speaking__buttons">
                {sentence.mp3 && (
                  <button
                    type="button"
                    className={`speaking__play${playingIndex === sentence.idx ? " is-playing" : ""}`}
                    onClick={() => play(sentence)}
                    aria-label="듣기"
                  >
                    ▶
                  </button>
                )}
                {isSupported && isEditable && (
                  <button
                    type="button"
                    className={`speaking__mic${isListening ? " is-listening" : ""}`}
                    onClick={() => record(sentence)}
                    disabled={listeningIndex !== null && !isListening}
                  >
                    {isListening ? "다 읽었어요" : <MicIcon />}
                  </button>
                )}
                {typeof shownScoreOf(spoken) === "number" && (
                  <span className={`speaking__score ${speakingScoreClass(shownScoreOf(spoken))}`}>
                    {shownScoreOf(spoken)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpeakingPanel;
